import React from 'react';
import {
  Container,
  Stack,
  Text,
  Group,
  Badge,
  LoadingOverlay,
  Card,
  Divider,
  Title,
  Flex,
  Button
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IconEdit, IconArrowLeft, IconTrash } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import PageTitle from 'components/PageTitle';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { MasterArea } from 'services/api';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import Dates from 'components/Dates';

const MasterAreaDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();

  const items = [
    { title: 'Master Area', href: '/app/dacum/master-area' },
    { title: 'Area Details', href: '' }
  ];

  // Fetch area details
  const {
    data: areaData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['masterArea', id],
    queryFn: () => masterAreaService.getDetail(id!),
    enabled: !!id,
    select: (response) => response as MasterArea
  });

  const deleteMutation = useMutation({
    mutationFn: (areaId: string) => masterAreaService.delete(areaId),
    onSuccess: () => {
      navigate('/app/dacum/master-area');
      showSuccessNotification({
        title: 'Deleted Successfully!',
        message: 'Area has been deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to delete area',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const handleEdit = () => {
    navigate(`/app/dacum/master-area/edit/${id}`);
  };

  const handleDelete = () => {
    openDelete();
  };

  const processDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
    closeDelete();
  };

  const handleBackToList = () => {
    navigate('/app/dacum/master-area');
  };

  // Handle error state
  if (error) {
    return (
      <Container fluid>
        <PageTitle title='Area Details' />
        <BreadcrumbsComponent items={items} />

        <Card shadow='xs' radius='md' mt={20} p='xl'>
          <Stack align='center' gap='md'>
            <Text size='lg' fw={500} c='red'>
              Error Loading Area Details
            </Text>
            <Text size='sm' c='dimmed'>
              The area you're looking for could not be found or there was an error loading the
              details.
            </Text>
            <Button
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBackToList}
              variant='light'
            >
              Back to Areas List
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  // Handle case where ID is not provided
  if (!id) {
    navigate('/app/dacum/master-area');
    return null;
  }

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={deleteOpened}
        open={openDelete}
        close={closeDelete}
        onSubmit={processDelete}
        itemLabel='area'
      />

      <PageTitle title='Area Details' />
      <BreadcrumbsComponent items={items} />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || deleteMutation.isPending} />

        {areaData && (
          <Stack gap='md' p='lg'>
            {/* Header with actions */}
            <Flex justify='space-between' align='flex-start' wrap='wrap' gap='md'>
              <div>
                <Title order={2} mb={4}>
                  {areaData.areaName}
                </Title>
                <Badge size='lg' variant='light' color='blue' radius='sm'>
                  {areaData.areaCode}
                </Badge>
              </div>

              <Group gap='sm'>
                <SecondaryButton
                  text='Back to List'
                  onClick={handleBackToList}
                  leftSection={<IconArrowLeft size={16} />}
                />
                <SecondaryButton
                  text='Edit'
                  onClick={handleEdit}
                  leftSection={<IconEdit size={16} />}
                />
                <PrimaryButton
                  text='Delete'
                  onClick={handleDelete}
                  isDelete
                  leftSection={<IconTrash size={16} />}
                />
              </Group>
            </Flex>

            <Divider />

            {/* Area Information */}
            <Stack gap='lg'>
              <div>
                <Text size='sm' fw={600} c='dimmed' mb={4}>
                  BASIC INFORMATION
                </Text>

                <Stack gap='md'>
                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Area ID:
                    </Text>
                    <Text size='sm' c='dimmed' style={{ fontFamily: 'monospace' }}>
                      {areaData.areaID}
                    </Text>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Area Code:
                    </Text>
                    <Badge variant='outline' size='sm'>
                      {areaData.areaCode}
                    </Badge>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Area Name:
                    </Text>
                    <Text size='sm'>{areaData.areaName}</Text>
                  </Group>
                </Stack>
              </div>

              <Divider />

              {/* Audit Information */}
              <div>
                <Text size='sm' fw={600} c='dimmed' mb={4}>
                  AUDIT INFORMATION
                </Text>

                <Stack gap='md'>
                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Created By:
                    </Text>
                    <Badge variant='light' color='green' size='sm'>
                      {areaData.userIn}
                    </Badge>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Created Date:
                    </Text>
                    <Dates date={areaData.dateIn} dateFormat='dateTime' fz={14} />
                  </Group>

                  {areaData.userUp && (
                    <Group>
                      <Text size='sm' fw={500} w={150}>
                        Updated By:
                      </Text>
                      <Badge variant='light' color='orange' size='sm'>
                        {areaData.userUp}
                      </Badge>
                    </Group>
                  )}

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Updated Date:
                    </Text>
                    <Dates date={areaData.dateUp} dateFormat='dateTime' fz={14} />
                  </Group>
                </Stack>
              </div>
            </Stack>
          </Stack>
        )}
      </Card>
    </Container>
  );
};

export default MasterAreaDetailPage;
